/**
 * Modern Subscription Card Component
 * Displays individual subscription plan with pricing, features, and action button
 * Uses primary color from theme palette for cohesive design
 */

import React, {useMemo} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
} from 'react-native';
import Animated, {ZoomIn} from 'react-native-reanimated';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius, shadows} from '~constants/design';
import {SubscriptionPlanDetails, SubscriptionType, SubscriptionFeature, SubscriptionPlan} from '~types/subscription/subscription.types';
import {SUBSCRIPTION_PLAN_ORDER, getPlanDetails} from '~constants/subscription';

interface SubscriptionCardProps {
  plan: SubscriptionPlanDetails;
  index: number;
  onSelectMonthly: (plan: SubscriptionPlanDetails) => void;
  isCurrentPlan?: boolean;
  currentPlanId?: string;
  onUpgradeDowngrade?: (plan: SubscriptionPlanDetails) => void;
  isProcessing?: boolean;
  allPlans?: SubscriptionPlanDetails[];
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  plan,
  index,
  onSelectMonthly,
  isCurrentPlan = false,
  currentPlanId,
  onUpgradeDowngrade,
  isProcessing = false,
  allPlans = [],
}) => {
  const {colors, themeMode} = useTheme();

  const isUpgrade = useMemo(() => {
    if (!currentPlanId) return false;
    
    // Try to find the current plan in the provided allPlans list (API plans)
    // Fall back to static getPlanDetails if not found
    const currentPlan = allPlans.find(p => p.id === currentPlanId) || 
                       getPlanDetails(currentPlanId as SubscriptionPlan);
                       
    if (!currentPlan || !currentPlan.pricing?.monthly) return false;
    
    return (plan.pricing?.monthly || 0) > currentPlan.pricing.monthly;
  }, [currentPlanId, plan.pricing, allPlans]);

  const isDowngrade = useMemo(() => {
    if (!currentPlanId) return false;
    
    const currentPlan = allPlans.find(p => p.id === currentPlanId) || 
                       getPlanDetails(currentPlanId as SubscriptionPlan);
                       
    if (!currentPlan || !currentPlan.pricing?.monthly) return false;
    
    return (plan.pricing?.monthly || 0) < currentPlan.pricing.monthly;
  }, [currentPlanId, plan.pricing, allPlans]);

  const styles = useMemo(() => {
    const baseCardStyle: ViewStyle = {
      borderRadius: borderRadius.xl,
      marginVertical: spacing.xs,
      marginHorizontal: 0,
      overflow: 'hidden',
      ...shadows.md,
      borderWidth: 2,
      borderColor: colors.border + '30',
    };

    const cardInnerStyle: ViewStyle = {
      padding: spacing.lg,
      backgroundColor: colors.surface,
    };

    const cardHighlightedStyle: ViewStyle = {
      ...baseCardStyle,
      borderWidth: 2.5,
      borderColor: colors.primary,
      ...shadows.lg,
      backgroundColor: colors.surface,
    };

    const cardPremiumStyle: ViewStyle = {
      ...baseCardStyle,
      borderWidth: 2.5,
      borderColor: colors.primary,
      ...shadows.lg,
      backgroundColor: colors.primary + '04',
    };

    const badgeStyle: ViewStyle = {
      position: 'absolute',
      top: spacing.sm,
      right: spacing.sm,
      backgroundColor: colors.primary,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      borderRadius: borderRadius.full,
      zIndex: 10,
      ...shadows.md,
    };

    const badgeTextStyle: TextStyle = {
      color: colors.textInverse,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.5,
    };

    const planNameStyle: TextStyle = {
      fontSize: 17,
      fontWeight: '800',
      color: colors.text,
      marginBottom: spacing.xs,
      letterSpacing: 0.3,
      marginRight: 0,
    };

    const planDescriptionStyle: TextStyle = {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: spacing.lg,
      lineHeight: 18,
    };

    const pricingSectionStyle: ViewStyle = {
      backgroundColor: colors.primary + '08',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      marginBottom: spacing.lg,
      borderWidth: 1,
      borderColor: colors.primary + '20',
    };

    const priceRowStyle: ViewStyle = {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.xs,
      paddingBottom: spacing.xs,
    };

    const priceRowLastStyle: ViewStyle = {
      marginBottom: 0,
      borderBottomWidth: 0,
    };

    const priceTypeStyle: TextStyle = {
      fontSize: 12,
      fontWeight: '500',
      color: colors.textSecondary,
    };

    const priceValueStyle: TextStyle = {
      fontSize: 16,
      fontWeight: '800',
      color: colors.primary,
      letterSpacing: 0.3,
    };

    const featureListStyle: ViewStyle = {
      marginBottom: spacing.md,
    };

    const featureItemStyle: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: spacing.xs,
      paddingVertical: spacing.xs,
    };

    const featureCheckStyle: TextStyle = {
      fontSize: 15,
      marginRight: spacing.sm,
      color: colors.primary,
      fontWeight: '700',
    };

    const featureTextContainerStyle: ViewStyle = {
      flex: 1,
    };

    const featureNameStyle: TextStyle = {
      fontSize: 12,
      fontWeight: '700',
      color: colors.text,
      marginBottom: 2,
      letterSpacing: 0.2,
    };

    const featureDescriptionStyle: TextStyle = {
      fontSize: 11,
      color: colors.textSecondary,
      lineHeight: 15,
    };

    const featureDisabledStyle: TextStyle = {
      opacity: 0.5,
    };

    const buttonContainerStyle: ViewStyle = {
      flexDirection: 'row',
      gap: spacing.xs,
      marginTop: spacing.md,
    };

    const buttonBaseStyle: ViewStyle = {
      flex: 1,
      paddingVertical: spacing.md,
      borderRadius: borderRadius.lg,
      justifyContent: 'center',
      alignItems: 'center',
      ...shadows.sm,
    };

    const buttonPrimaryStyle: ViewStyle = {
      ...buttonBaseStyle,
      backgroundColor: colors.primary,
    };

    const buttonSecondaryStyle: ViewStyle = {
      ...buttonBaseStyle,
      backgroundColor: colors.background,
      borderWidth: 1.5,
      borderColor: colors.primary + '40',
    };

    const buttonTextStyle: TextStyle = {
      fontWeight: '800',
      fontSize: 13,
      letterSpacing: 0.5,
    };

    const currentPlanBadgeStyle: ViewStyle = {
      marginTop: spacing.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      backgroundColor: colors.primary + '15',
      borderRadius: borderRadius.lg,
      borderWidth: 1.5,
      borderColor: colors.primary + '50',
      alignItems: 'center',
    };

    const currentPlanTextStyle: TextStyle = {
      fontSize: 12,
      fontWeight: '700',
      color: colors.primary,
      letterSpacing: 0.3,
    };

    return {
      baseCardStyle,
      cardInnerStyle,
      cardHighlightedStyle,
      cardPremiumStyle,
      badgeStyle,
      badgeTextStyle,
      planNameStyle,
      planDescriptionStyle,
      pricingSectionStyle,
      priceRowStyle,
      priceRowLastStyle,
      priceTypeStyle,
      priceValueStyle,
      featureListStyle,
      featureItemStyle,
      featureCheckStyle,
      featureTextContainerStyle,
      featureNameStyle,
      featureDescriptionStyle,
      featureDisabledStyle,
      buttonContainerStyle,
      buttonPrimaryStyle,
      buttonSecondaryStyle,
      buttonTextStyle,
      currentPlanBadgeStyle,
      currentPlanTextStyle,
    };
  }, [colors, themeMode, plan.badge]);

  const renderFeatureItem = ({item}: {item: SubscriptionFeature}) => (
    <View style={styles.featureItemStyle}>
      <Text style={[styles.featureCheckStyle, !item.included && {opacity: 0.5}]}>
        {item.included ? '✓' : '○'}
      </Text>
      <View style={styles.featureTextContainerStyle}>
        <Text
          style={[
            styles.featureNameStyle,
            !item.included && styles.featureDisabledStyle,
          ]}>
          {item.name}
        </Text>
        {/*<Text
          style={[
            styles.featureDescriptionStyle,
            !item.included && styles.featureDisabledStyle,
          ]}>
          {item.description}
        </Text> */}
      </View>
    </View>
  );

  return (
    <Animated.View
      entering={ZoomIn.delay(index * 100).duration(500)}
      style={
        plan.highlighted
          ? styles.cardHighlightedStyle
          : index === 1
          ? styles.cardPremiumStyle
          : styles.baseCardStyle
      }>
      <View style={styles.cardInnerStyle}>
        {plan.badge && (
          <View style={styles.badgeStyle}>
            <Text style={styles.badgeTextStyle}>{plan.badge}</Text>
          </View>
        )}

        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <Text style={styles.planNameStyle}>{plan.name}</Text>
        </View>
        <Text style={styles.planDescriptionStyle}>{plan.description}</Text>

        {/* Pricing Section */}
        <View style={styles.pricingSectionStyle}>
          {plan.pricing.monthly !== undefined && (
            <View style={[styles.priceRowStyle, styles.priceRowLastStyle]}>
              <Text style={styles.priceTypeStyle}>Monthly Subscription</Text>
              {isCurrentPlan ? (
                <Text style={{...styles.priceValueStyle, color: colors.primary}}>
                  Included
                </Text>
              ) : (
                <Text style={styles.priceValueStyle}>
                  {plan.pricing.currency}
                  {plan.pricing.monthly}
                </Text>
              )}
            </View>
          )}
        </View>

        {/* Features List */}
        <View style={styles.featureListStyle}>
          <FlatList
            data={plan.features.filter((item) => item.included !== false)}
            renderItem={renderFeatureItem}
            keyExtractor={item => item.id}
            scrollEnabled={false}
          />
        </View>

        {/* Action Buttons */}
        {!isCurrentPlan && (
          <View style={[styles.buttonContainerStyle, {flex: 1}]}>
            <TouchableOpacity
              style={styles.buttonPrimaryStyle}
              onPress={() => {
                if (currentPlanId && onUpgradeDowngrade) {
                  onUpgradeDowngrade(plan);
                } else {
                  onSelectMonthly(plan);
                }
              }}
              disabled={isProcessing}
              activeOpacity={0.85}>
              {isProcessing ? (
                <ActivityIndicator size="small" color={colors.textInverse} />
              ) : (
                <Text style={[styles.buttonTextStyle, {color: colors.textInverse}]}>
                  {currentPlanId 
                    ? (isUpgrade ? 'Upgrade' : 'Downgrade') 
                    : `${plan.pricing.currency}${plan.pricing.monthly}`}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {isCurrentPlan && (
          <View style={styles.currentPlanBadgeStyle}>
            <Text style={styles.currentPlanTextStyle}>
              ✓ Your Current Plan
            </Text>
          </View>
        )}
      </View>
    </Animated.View>
  );
};

export default SubscriptionCard;
